/**
 * Atelier Mino — animation du logo au changement de variante (14/10/2026, demande de Thomas : sur toutes les fiches sauf les
 * protège-passeports, qui gardent l'aiguille de couture).
 * Quand la cliente change une option qui change la photo (Coloris, Forme…, liste dans data-options), un voile crème se pose sur
 * l'image visible et la boucle du logo s'y trace (tracé, tête lumineuse, point corail : les styles du snippet mino-logo, déjà dans
 * l'en-tête). Le voile reste au moins 1,1 s, s'efface dès que la nouvelle photo est chargée, et au plus tard après 2,5 s.
 * Horizon remplace la galerie au changement de variante : le voile est reposé dans la nouvelle image visible.
 * Décor seulement : rien ne l'attend, aucun clic n'est bloqué. Mouvement réduit : rien.
 * Chargé par le bloc mino-confiance-fiche (marqueur [data-mino-logo-variante]).
 */
(() => {
  if (window.MinoLogoVariante) return;
  const reduit = matchMedia('(prefers-reduced-motion: reduce)');
  const MINI = 1100, MAXI = 2500, EFFACE = 250, BOUCLE = 1600;   // 1,1 s : la boucle et le point corail ont le temps de se poser
  // styles du voile injectés par le script (pas d'attente de la recompilation des {% stylesheet %} du thème)
  if (!document.getElementById('mino-lv-styles')) {
    const st = document.createElement('style');
    st.id = 'mino-lv-styles';
    st.textContent = '.mino-lv{position:absolute;inset:0;z-index:3;'
      + 'background:rgb(255 249 245 / .78);opacity:0;transition:opacity .25s ease;pointer-events:none}'
      + '.mino-lv.is-visible{opacity:1}'
      + '.mino-lv svg.mino-lv__logo{position:absolute;left:50%;top:40%;transform:translateX(-50%);display:block;width:clamp(72px,26%,140px);'
      + 'height:auto;max-width:none;color:var(--mino-prune,#3B2340);overflow:visible}';
    document.head.appendChild(st);
  }

  /** Boucle du logo (sans le nom) : copie des tracés du logo de l'en-tête, recadrée sur le symbole. */
  function symbole() {
    const src = document.querySelector('svg.mino-logo');
    if (!src) return null;
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '-10 -90 132 100');
    svg.setAttribute('class', 'mino-logo mino-lv__logo');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    src.querySelectorAll('.mino-logo__trace, .mino-logo__tete, .mino-logo__point').forEach((n) => svg.appendChild(n.cloneNode(true)));
    return svg.childNodes.length ? svg : null;
  }

  class LogoVariante {
    constructor(marqueur) {
      this.options = (marqueur.dataset.options || '').split('|').map((x) => x.trim().toLowerCase()).filter(Boolean);
      this.section = marqueur.closest('.shopify-section') || document.body;
      this.section.addEventListener('change', (e) => {
        const r = e.target;
        if (!(r instanceof HTMLInputElement) || r.type !== 'radio' || !r.closest('variant-picker')) return;
        if (!this.options.includes((r.dataset.optionName || '').toLowerCase())) return;
        this.demarrer();
      });
      new MutationObserver(() => this.suivre()).observe(this.section, { childList: true, subtree: true });
    }

    /** Image visible : 1re de la grille (ordinateur) ou diapositive affichée (mobile), hors loupe. */
    cadre() {
      for (const c of this.section.querySelectorAll('media-gallery .product-media[data-media-id]')) {
        if (c.closest('dialog') || c.closest('slideshow-slide')?.getAttribute('aria-hidden') === 'true') continue;
        if (c.getBoundingClientRect().width > 0) return c;
      }
      return null;
    }

    demarrer() {
      if (reduit.matches) return;
      const cadre = this.cadre();
      if (!cadre) return;
      this.retirer(true);
      const voile = document.createElement('div');
      voile.className = 'mino-lv';
      voile.setAttribute('aria-hidden', 'true');
      const logo = symbole();
      if (logo) voile.appendChild(logo);
      this.etat = { voile, t0: performance.now(), avant: cadre.dataset.mediaId, src: cadre.querySelector('img')?.currentSrc || '' };
      cadre.appendChild(voile);
      this.placer();
      requestAnimationFrame(() => voile.classList.add('is-visible'));
      this.defile = () => this.placer();
      document.addEventListener('scroll', this.defile, { passive: true, capture: true });
      this.minuteries = [
        setInterval(() => this.verifier(), 100),
        setInterval(() => this.rejouer(), BOUCLE),
        setTimeout(() => this.retirer(), MAXI),
      ];
    }

    /** Le logo se place au centre de la partie visible de la photo (la cliente choisit souvent une option plus bas sur la page). */
    placer() {
      const v = this.etat?.voile, logo = v?.querySelector('.mino-lv__logo');
      if (!logo || !v.isConnected) return;
      const r = v.getBoundingClientRect();
      const haut = Math.max(r.top, 0), bas = Math.min(r.bottom, innerHeight);
      const centre = bas > haut + 40 ? (haut + bas) / 2 - r.top : r.height / 2;
      const demi = logo.getBoundingClientRect().height / 2 || 40;
      logo.style.top = `${Math.round(Math.min(Math.max(centre, demi + 8), r.height - demi - 8) - demi)}px`;
    }

    /** Chargement long : la boucle se retrace (nouveaux éléments = animation relancée). */
    rejouer() {
      const v = this.etat?.voile, logo = v && symbole();
      if (!logo) return;
      v.querySelector('.mino-lv__logo')?.remove();
      v.appendChild(logo);
      this.placer();
    }

    /** Galerie remplacée par Horizon : le voile passe dans la nouvelle image visible. */
    suivre() {
      const v = this.etat?.voile;
      if (!v || v.isConnected) return;
      const cadre = this.cadre();
      if (cadre) { cadre.appendChild(v); this.placer(); }
    }

    /** Nouvelle photo affichée et chargée (ou image identique après 1,2 s) : effacement, 1,1 s après le clic au plus tôt. */
    verifier() {
      const e = this.etat;
      if (!e) return;
      this.suivre();
      const cadre = this.cadre(), img = cadre?.querySelector('img');
      const ecoule = performance.now() - e.t0;
      const nouvelle = cadre && (cadre.dataset.mediaId !== e.avant || (img && img.currentSrc && img.currentSrc !== e.src));
      const chargee = img && img.complete && img.naturalWidth > 0;
      if (ecoule >= MINI && ((nouvelle && chargee) || (!nouvelle && ecoule >= 1200))) this.retirer();
    }

    retirer(immediat = false) {
      (this.minuteries || []).forEach((m) => { clearInterval(m); clearTimeout(m); });
      this.minuteries = [];
      if (this.defile) document.removeEventListener('scroll', this.defile, { capture: true });
      this.defile = null;
      const v = this.etat?.voile;
      this.etat = null;
      if (!v) return;
      if (immediat) { v.remove(); return; }
      v.classList.remove('is-visible');
      setTimeout(() => v.remove(), EFFACE);
    }
  }

  function demarrer() {
    document.querySelectorAll('[data-mino-logo-variante]').forEach((m) => {
      if (m.minoLogoVariante) return;
      m.minoLogoVariante = new LogoVariante(m);
    });
  }
  window.MinoLogoVariante = { demarrer };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', demarrer);
  else demarrer();
  document.addEventListener('shopify:section:load', demarrer);
})();
